import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/why-africa', label: 'Why Africa' },
  { to: '/ecosystem', label: 'Ecosystem' },
  { to: '/partners', label: 'Partners' },
  { to: '/climate-impact', label: 'Impact' },
  { to: '/opportunity', label: 'Opportunity' },
  { to: '/faq', label: 'FAQ' },
  { to: '/news', label: 'News' },
];

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  return (
    <>
      <nav className={`fixed inset-x-0 top-0 z-[1000] px-8 transition-all duration-300 ${
        scrolled
          ? 'border-b border-border bg-white/95 backdrop-blur-md shadow-card'
          : 'bg-bg-dark'
      }`}>
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <img
              src={scrolled ? '/images/logo.png' : '/images/logo-white.png'}
              alt="Azura Mobility"
              className="h-[108px] w-[108px] object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden items-center gap-1 min-[901px]:flex">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-[0.82rem] font-medium transition duration-200 ${
                  scrolled
                    ? location.pathname === link.to
                      ? 'bg-accent-amber/15 text-bg-dark font-bold'
                      : 'text-bg-dark/65 hover:bg-neutral-100 hover:text-bg-dark'
                    : location.pathname === link.to
                      ? 'bg-white/15 text-white font-bold'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Invest CTA — always stands out */}
            <Link
              to="/invest"
              className="ml-3 inline-flex items-center gap-2 rounded-full bg-accent-amber px-5 py-2.5 font-figtree font-black text-xs uppercase tracking-wider text-bg-dark shadow-amber-sm transition-all hover:scale-105 hover:bg-white hover:shadow-card active:scale-95"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-bg-dark/70 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-bg-dark" />
              </span>
              Invest Now <ArrowRight size={12} />
            </Link>
          </div>

          {/* Mobile burger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`block cursor-pointer border-0 bg-transparent min-[901px]:hidden ${scrolled ? 'text-bg-dark' : 'text-white'}`}
            aria-label="Toggle navigation"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="fixed inset-x-0 bottom-0 top-[72px] z-[999] flex flex-col gap-2 bg-white/98 p-8 backdrop-blur-xl min-[901px]:hidden">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`rounded-xl border px-5 py-3.5 font-figtree text-lg font-semibold ${
                location.pathname === link.to
                  ? 'border-accent-amber/30 bg-accent-amber/10 text-bg-dark'
                  : 'border-border bg-neutral-50 text-bg-dark'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/invest"
            className="mt-2 flex items-center justify-center gap-2 rounded-full bg-accent-amber px-5 py-3.5 font-figtree text-base font-bold uppercase tracking-wider text-bg-dark shadow-amber-sm"
          >
            Invest Now <ArrowRight size={16} />
          </Link>
        </div>
      )}
    </>
  );
};

export default Navbar;
